// 认证状态仓库（Pinia）
import { defineStore } from "pinia";
import { ref, computed, watch, readonly } from "vue";
import type { User } from "@/types/user";
import { api } from "@/api";
import { getSSOUser } from "@/api/auth";
import { jwtService } from "@/services/jwt";
import { jwtLifecycleService } from "@/services/jwtLifecycleService";
import { errorRecoveryService } from "@/services/errorRecoveryService";
import {
  reportError,
  reportMessage,
  updateSentryUser,
  clearSentryUser,
} from "@/utils/sentry";

// 微信换绑状态
interface WechatRebindingState {
  isActive: boolean;
  originalTokens: { access: string; refresh: string } | null;
  openidForRebinding: string | null;
  startTime: number | null;
}

export const useAuthStore = defineStore(
  "auth",
  () => {
    // 核心状态
    const user = ref<User | null>(null);
    const isLoading = ref(false);
    const lastActivity = ref(Date.now());
    const sessionStartTime = ref(Date.now());

    // 微信换绑
    const wechatRebinding = ref<WechatRebindingState>({
      isActive: false,
      originalTokens: null,
      openidForRebinding: null,
      startTime: null,
    });

    // 派生状态
    const isAuthenticated = computed(() => {
      return !!user.value && !!jwtService.getToken();
    });

    const userRole = computed(() => {
      if (!user.value) return null;

      // 优先用 JWT 的 role，否则按原逻辑映射
      if (user.value.role) {
        return user.value.role;
      }

      // Fallback to original logic for backward compatibility
      if (user.value.is_superuser) return "admin";
      if (user.value.is_staff) return "staff";
      return "user";
    });

    const sessionDuration = computed(() => {
      return Date.now() - sessionStartTime.value;
    });

    const hasValidSession = computed(() => {
      return isAuthenticated.value && jwtService.hasValidTokens();
    });

    const userDisplayName = computed(() => {
      return user.value?.name || user.value?.username || "";
    });

    const isWechatRebinding = computed(() => wechatRebinding.value.isActive);

    // 活动跟踪
    function updateActivity(): void {
      lastActivity.value = Date.now();
      jwtLifecycleService.notifyActivity();
    }

    // 认证状态变化监听
    watch(isAuthenticated, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        console.log("Authentication state changed:", newValue);

        if (newValue) {
          updateSentryUser();
          jwtLifecycleService.start();
          reportMessage("用户认证状态激活", "info", {
            username: user.value?.username,
            sessionDuration: sessionDuration.value,
          });
        } else {
          clearSentryUser();
          jwtLifecycleService.stop();
          reportMessage("用户认证状态清除", "info");
        }
      }
    });

    // 认证行为
    async function login(
      username: string,
      password: string,
      fingerprint?: string,
    ) {
      console.log("Starting login process for user:", username);
      isLoading.value = true;

      try {
        const response = await api.post("/login/", {
          username,
          password,
          f: fingerprint,
        });

        console.log("Full login response:", response.data);

        if (response.data.access && response.data.refresh) {
          // 设置令牌
          await jwtService.setTokens(
            response.data.access,
            response.data.refresh,
          );

          // 解析 JWT 获取基本信息
          const payload = jwtService.parseToken(response.data.access);
          console.log("JWT payload for user data:", payload);

          if (!payload) {
            throw new Error("Invalid JWT token payload");
          }

          // 从 GetUser API 获取完整信息
          const userInfo = await getSSOUser();

          // 合并 JWT 与 API 信息
          user.value = {
            username: payload.username,
            name: payload.name,
            grade: payload.grade,
            major_class: payload.major_class,
            role: payload.role,
            is_staff: payload.is_staff,
            is_superuser: payload.is_superuser,
            email: userInfo.email,
            phone_number: userInfo.phone_number,
            is_active: userInfo.is_active,
          };

          console.log("Final user data set:", user.value);

          // 刷新会话跟踪
          sessionStartTime.value = Date.now();
          updateActivity();

          // 启动 JWT 生命周期
          jwtLifecycleService.start();

          console.log("Login successful, user data set:", user.value);

          reportMessage(`用户 ${username} 登录成功`, "info", {
            username: payload.username,
            name: payload.name,
            grade: payload.grade,
            major_class: payload.major_class,
            role: payload.role,
            is_staff: payload.is_staff,
            is_superuser: payload.is_superuser,
            userRole: userRole.value,
            sessionStart: sessionStartTime.value,
            action: "login_success",
            timestamp: new Date().toISOString(),
          });

          return response;
        } else {
          throw new Error("Invalid login response: missing tokens");
        }
      } catch (error: unknown) {
        console.error("Login failed:", error);

        reportError(error as Error, {
          type: "auth_login_error",
          action: "login",
          username,
          timestamp: new Date().toISOString(),
        });

        throw error;
      } finally {
        isLoading.value = false;
      }
    }

    async function logout(isManual = true): Promise<void> {
      try {
        const currentUser = user.value;
        const sessionLength = sessionDuration.value;

        // Log the logout attempt
        if (currentUser) {
          reportMessage(`用户 ${currentUser.username} 登出`, "info", {
            username: currentUser.username,
            sessionDuration: sessionLength,
            isManual,
            action: "logout_start",
          });
        }

        // 停止 JWT 生命周期
        jwtLifecycleService.stop();

        // 清理认证相关状态
        await clearAuthState();

        // 使用恢复服务做兜底清理
        await errorRecoveryService.forceRecovery({
          reason: isManual ? "manual_logout" : "auto_logout",
          redirectPath: "/login",
          userInitiated: isManual,
          showMessage: false,
        });
      } catch (error) {
        console.error("Logout failed:", error);
        reportError(error as Error, {
          type: "auth_logout_error",
          action: "logout",
          isManual,
        });

        // 出错也强制清理
        await clearAuthState();

        // 退回登录页
        const baseUrl = import.meta.env.VITE_BASE_URL || "/";
        window.location.href = `${baseUrl}login?manual_logout=${isManual}`;
      }
    }

    // 清空认证状态
    async function clearAuthState(): Promise<void> {
      // Clear user state
      user.value = null;

      // 重置会话追踪
      sessionStartTime.value = Date.now();
      lastActivity.value = Date.now();

      // 清空换绑状态
      wechatRebinding.value = {
        isActive: false,
        originalTokens: null,
        openidForRebinding: null,
        startTime: null,
      };

      // 清除令牌
      jwtService.clearTokens();

      // 清除 Sentry 用户上下文
      clearSentryUser();
    }

    async function fetchUserFromToken(): Promise<void> {
      console.log("Fetching user information from token");

      try {
        const token = jwtService.getToken();
        if (!token) {
          throw new Error("No JWT token found");
        }

        // 确保令牌有效
        const isValid = await jwtService.ensureValidTokens();
        if (!isValid) {
          throw new Error("Token validation failed");
        }

        const payload = jwtService.parseToken(token);
        if (!payload) {
          throw new Error("Invalid JWT token payload");
        }

        // 获取额外用户信息
        const userInfo = await getSSOUser();

        // 合并信息
        user.value = {
          username: payload.username,
          name: payload.name,
          grade: payload.grade,
          major_class: payload.major_class,
          role: payload.role,
          is_staff: payload.is_staff,
          is_superuser: payload.is_superuser,
          email: userInfo.email,
          phone_number: userInfo.phone_number,
          is_active: userInfo.is_active,
        };

        // 刷新活动并启动生命周期
        updateActivity();
        jwtLifecycleService.start();

        console.log(
          "User information updated from JWT and GetUser API:",
          user.value,
        );

        reportMessage("从 JWT 和 GetUser API 获取用户信息成功", "info", {
          username: payload.username,
          name: payload.name,
          grade: payload.grade,
          major_class: payload.major_class,
          role: payload.role,
          userRole: userRole.value,
          action: "fetch_user_from_jwt_and_api",
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Failed to fetch user from token:", error);

        reportError(error as Error, {
          type: "auth_fetch_user_error",
          action: "fetch_user_from_jwt_and_api",
          timestamp: new Date().toISOString(),
        });

        // 清理失效状态并触发恢复
        await clearAuthState();

        // 检测是否陷入异常流并恢复
        if (errorRecoveryService.detectTrappedUser()) {
          await errorRecoveryService.forceRecovery({
            reason: "token_fetch_failure",
            redirectPath: "/login",
          });
        }
      }
    }

    // 用户信息更新
    async function updateUserField(field: keyof User, value: string | boolean) {
      if (!user.value) {
        throw new Error("No user logged in");
      }

      try {
        const response = await api.post(`/modify/${field}/`, {
          [field]: value,
        });

        if (response.status === 200) {
          // 更新本地数据
          if (user.value) {
            (user.value as Record<string, unknown>)[field] = value;
          }

          // 刷新活动
          updateActivity();

          // 关键字段更新同步 Sentry
          if (field === "username" || field === "email" || field === "name") {
            updateSentryUser();
            reportMessage(`用户更新了 ${field}`, "info", {
              field,
              username: user.value.username,
              action: "update_user_field",
            });
          }
        }

        return response;
      } catch (error: unknown) {
        reportError(error as Error, {
          type: "auth_update_user_error",
          action: "update_user_field",
          field,
          username: user.value?.username,
        });
        throw error;
      }
    }

    async function changePassword(oldPassword: string, newPassword: string) {
      if (!user.value) {
        throw new Error("No user logged in");
      }

      isLoading.value = true;

      try {
        const response = await api.post("/modify/password/", {
          old_password: oldPassword,
          new_password: newPassword,
        });

        reportMessage("密码修改成功，需要重新登录", "info", {
          username: user.value.username,
          action: "password_change_success",
        });

        // Force logout after password change
        await logout(false);

        return response;
      } catch (error: unknown) {
        reportError(error as Error, {
          type: "auth_password_change_error",
          action: "change_password",
          username: user.value?.username,
        });
        throw error;
      } finally {
        isLoading.value = false;
      }
    }

    async function updateEmail(email: string) {
      isLoading.value = true;
      try {
        return await updateUserField("email", email);
      } finally {
        isLoading.value = false;
      }
    }

    async function updatePhone(phoneNumber: string) {
      isLoading.value = true;
      try {
        return await updateUserField("phone_number", phoneNumber);
      } finally {
        isLoading.value = false;
      }
    }

    // 密码重置
    async function requestPasswordReset(username: string, email: string) {
      isLoading.value = true;

      try {
        await api.post("/code/", { username, email });

        reportMessage(`用户 ${username} 请求密码重置`, "info", {
          username,
          email,
          action: "password_reset_request",
        });
      } catch (error: unknown) {
        reportError(error as Error, {
          type: "auth_password_reset_request_error",
          action: "password_reset_request",
          username,
          email,
        });
        throw error;
      } finally {
        isLoading.value = false;
      }
    }

    async function resetPassword(
      username: string,
      code: string,
      newPassword: string,
    ) {
      isLoading.value = true;

      try {
        await api.post("/reset/password/", {
          username,
          code,
          new_password: newPassword,
        });

        reportMessage(`用户 ${username} 密码重置成功`, "info", {
          username,
          action: "password_reset_success",
        });
      } catch (error: unknown) {
        reportError(error as Error, {
          type: "auth_password_reset_error",
          action: "password_reset",
          username,
        });
        throw error;
      } finally {
        isLoading.value = false;
      }
    }

    // 微信换绑
    function startWechatRebinding(wechatOpenid: string): void {
      console.log("Starting WeChat rebinding process");

      const accessToken = jwtService.getToken();
      const refreshToken = jwtService.getRefreshToken();

      wechatRebinding.value = {
        isActive: true,
        originalTokens:
          accessToken && refreshToken
            ? { access: accessToken, refresh: refreshToken }
            : null,
        openidForRebinding: wechatOpenid,
        startTime: Date.now(),
      };

      reportMessage("开始微信换绑流程", "info", {
        action: "start_wechat_rebinding",
        openid: wechatOpenid.substring(0, 8) + "***",
        hasOriginalTokens: !!wechatRebinding.value.originalTokens,
        username: user.value?.username,
        timestamp: new Date().toISOString(),
      });
    }

    async function cancelWechatRebinding(): Promise<void> {
      console.log("Cancelling WeChat rebinding, restoring original account");

      try {
        // Restore original tokens if available
        if (wechatRebinding.value.originalTokens) {
          await jwtService.setTokens(
            wechatRebinding.value.originalTokens.access,
            wechatRebinding.value.originalTokens.refresh,
          );

          // Refetch user information
          await fetchUserFromToken();
        }

        reportMessage("微信换绑流程已取消，恢复原账号", "info", {
          action: "cancel_wechat_rebinding",
          username: user.value?.username,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error("Error during WeChat rebinding cancellation:", error);
        reportError(error as Error, {
          type: "auth_wechat_rebinding_error",
          action: "cancel_wechat_rebinding",
        });
      } finally {
        // Clear rebinding state
        wechatRebinding.value = {
          isActive: false,
          originalTokens: null,
          openidForRebinding: null,
          startTime: null,
        };
      }
    }

    async function completeWechatRebinding(): Promise<boolean> {
      console.log("Completing WeChat rebinding process");

      if (!wechatRebinding.value.openidForRebinding) {
        throw new Error("No WeChat openid for rebinding found");
      }

      if (!user.value?.username) {
        throw new Error("No user information found");
      }

      try {
        const { bindWechatOpenid } = await import("@/api/wechat");

        const bindResult = await bindWechatOpenid(
          wechatRebinding.value.openidForRebinding,
          user.value.username,
        );

        if (bindResult.success) {
          // Update tokens if provided
          if (bindResult.access && bindResult.refresh) {
            await jwtService.setTokens(bindResult.access, bindResult.refresh);
          }

          reportMessage("微信换绑完成", "info", {
            action: "complete_wechat_rebinding",
            username: user.value.username,
            openid:
              wechatRebinding.value.openidForRebinding.substring(0, 8) + "***",
            timestamp: new Date().toISOString(),
          });

          // Clear rebinding state
          wechatRebinding.value = {
            isActive: false,
            originalTokens: null,
            openidForRebinding: null,
            startTime: null,
          };

          return true;
        } else {
          throw new Error(bindResult.message || "微信绑定失败");
        }
      } catch (error: unknown) {
        reportError(error as Error, {
          type: "auth_wechat_rebinding_error",
          action: "complete_wechat_rebinding",
          username: user.value?.username,
          openid: wechatRebinding.value.openidForRebinding
            ? wechatRebinding.value.openidForRebinding.substring(0, 8) + "***"
            : "unknown",
        });
        throw error;
      }
    }

    // 工具函数
    function refreshActivity(): void {
      updateActivity();
    }

    async function validateSession(): Promise<boolean> {
      try {
        const isValid = await jwtService.ensureValidTokens();
        if (isValid) {
          updateActivity();
          return true;
        }
        return false;
      } catch (error) {
        console.error("Session validation failed:", error);
        return false;
      }
    }

    // Export store interface
    return {
      // State
      user: readonly(user),
      isLoading: readonly(isLoading),
      lastActivity: readonly(lastActivity),
      sessionStartTime: readonly(sessionStartTime),

      // Computed getters
      isAuthenticated,
      userRole,
      sessionDuration,
      hasValidSession,
      userDisplayName,
      isWechatRebinding,

      // Core authentication actions
      login,
      logout,
      clearAuthState,
      fetchUserFromToken,

      // User management actions
      updateUserField,
      changePassword,
      updateEmail,
      updatePhone,

      // Password reset actions
      requestPasswordReset,
      resetPassword,

      // WeChat rebinding actions
      startWechatRebinding,
      cancelWechatRebinding,
      completeWechatRebinding,

      // Utility actions
      refreshActivity,
      validateSession,
      updateActivity,

      // Legacy aliases for backward compatibility
      handleLogin: login,
      handleLogout: logout,
      fetchUser: fetchUserFromToken,
      handlePasswordChange: changePassword,
      handleEmailModification: updateEmail,
      handlePhoneModification: updatePhone,
      handlePasswordResetRequest: requestPasswordReset,
      handlePasswordReset: resetPassword,

      // WeChat rebinding legacy aliases
      originalTokens: computed(() => wechatRebinding.value.originalTokens),
      wechatOpenidForRebinding: computed(
        () => wechatRebinding.value.openidForRebinding,
      ),
      loading: isLoading, // Legacy alias
    };
  },
  {
    // Enable persistence for critical state
    persist: {
      key: "auth-store",
      storage: sessionStorage,
      paths: ["sessionStartTime", "lastActivity"],
    },
  },
);
